import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, Sun, Moon, Leaf, ShieldCheck, X, Heart } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const NAV = [
  { label: "Cuidado Personal", path: "/categoria/cuidado-personal" },
  { label: "Hogar Limpio", path: "/categoria/hogar-limpio" },
  { label: "Nutrición Limpia", path: "/categoria/nutricion-limpia" },
  { label: "Longevidad", path: "/categoria/longevidad-celular" },
  { label: "Catálogo", path: "/catalogo" },
  { label: "Guías", path: "/blog" },
];

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { count } = useFavorites();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [leadOpen, setLeadOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/catalogo?search=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-border bg-background/70 glass"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0 group">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950">
              <Leaf className="h-5 w-5" strokeWidth={2.4} />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-display font-extrabold tracking-tight text-lg">ZeroToxic<span className="text-emerald-400">Market</span></span>
              <span className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-emerald-500/80">Bio-Curated</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.path}
                to={n.path}
                data-testid={`nav-${n.path.split("/").pop()}`}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-emerald-400 transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
              <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar producto…"
                className="h-9 w-40 lg:w-48 rounded-full border border-border bg-secondary/50 pl-9 pr-3 text-sm outline-none focus:border-emerald-500/60 transition-colors"
              />
            </form>

            <Button
              data-testid="header-guide-btn"
              onClick={() => setLeadOpen(true)}
              className="hidden sm:inline-flex bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-bold rounded-full"
            >
              Guía 30 Días
            </Button>

            <Link
              to="/favoritos"
              data-testid="header-favorites-link"
              className="relative h-9 w-9 flex items-center justify-center rounded-full border border-border hover:border-emerald-500/60 transition-colors"
              aria-label="Favoritos"
            >
              <Heart className="h-4 w-4" />
              {count > 0 && (
                <span data-testid="fav-count" className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[9px] font-bold text-slate-950">
                  {count}
                </span>
              )}
            </Link>

            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="h-9 w-9 flex items-center justify-center rounded-full border border-border hover:border-emerald-500/60 transition-colors"
              aria-label="Cambiar tema"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full border border-border">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-border w-80">
                <div className="flex items-center gap-2 mb-6">
                  <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  <span className="font-display font-bold">Menú</span>
                </div>
                <form onSubmit={submitSearch} className="relative mb-4">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <input
                    data-testid="mobile-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar producto…"
                    className="h-10 w-full rounded-lg border border-border bg-secondary/50 pl-9 pr-3 text-sm outline-none"
                  />
                </form>
                <nav className="flex flex-col gap-1">
                  {NAV.map((n) => (
                    <Link
                      key={n.path}
                      to={n.path}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${n.path.split("/").pop()}`}
                      className="px-3 py-3 rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
                    >
                      {n.label}
                    </Link>
                  ))}
                </nav>
                <Button
                  onClick={() => { setLeadOpen(true); setMobileOpen(false); }}
                  className="mt-4 w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold"
                >
                  Descargar Guía 30 Días
                </Button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <LeadCaptureModal open={leadOpen} onOpenChange={setLeadOpen} />
    </header>
  );
};

export default Header;
